import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useModeTokens } from '@/common/components/ui';
import ProjectCard from './ProjectCard';
import CardSizeToggle from './CardSizeToggle';
import UploadProjectCard from './UploadProjectCard';
import { useProjectsContext } from '../context/ProjectsContext';
import { useBatchUploadHandler } from '../hooks/BatchUploadHandler';
import { GetProjectsQueryData } from '@/generated/graphql';
import { FadeContent } from '@/common/components/reactBits';

type ProjectListItem = GetProjectsQueryData['projects'][number];

interface ProjectsGridProps {
  projects: ProjectListItem[];
}

const ProjectsGrid: React.FC<ProjectsGridProps> = ({ projects }) => {
  const t = useModeTokens();
  const isDark = useTheme().palette.mode === 'dark';
  const { cardSize, setCardSize, isScanning } = useProjectsContext();
  const { handleBatchFolderSelect } = useBatchUploadHandler();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      ),
    [projects]
  );

  useEffect(() => {
    // Keep an up-to-date width for responsive toggle filtering.
    const updateWidth = () => {
      setGridWidth(
        gridRef.current
          ? Math.max(0, gridRef.current.clientWidth - 24)
          : window.innerWidth
      );
    };

    updateWidth();

    // Prefer element-level observation so calculations track container changes,
    // not only global viewport changes.
    if (typeof ResizeObserver !== 'undefined' && gridRef.current) {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(gridRef.current);
      return () => observer.disconnect();
    }

    // Fallback for environments without ResizeObserver.
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontSize: 13, fontWeight: 600, color: t.text.bright }}
        >
          Recent projects
        </Typography>
        <CardSizeToggle
          cardSize={cardSize}
          setCardSize={setCardSize}
          containerWidth={gridWidth}
        />
      </Box>

      {/* Projects Grid */}
      <Box
        ref={gridRef}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: `repeat(auto-fill, minmax(min(${cardSize}px, 100%), 1fr))`,
            sm: `repeat(auto-fill, minmax(${cardSize}px, 1fr))`,
          },
          gap: 2,
          width: '100%',
          p: { xs: 2, sm: 1.5 },
          borderRadius: '12px',
          backgroundColor: isDark ? 'transparent' : t.surfaces.screen,
          border: `1px solid ${isDark ? 'transparent' : t.borders.hairline}`,
          boxShadow: isDark ? 'none' : t.shadows.controlInset,
        }}
      >
        {sortedProjects?.map((project, index) => (
          <FadeContent
            key={project._id}
            delay={Math.min(index * 55, 220)}
            duration={520}
            style={{ height: '100%', minWidth: 0 }}
          >
            <ProjectCard project={project} />
          </FadeContent>
        ))}

        <FadeContent
          delay={Math.min(sortedProjects.length * 55, 260)}
          duration={520}
          style={{ height: '100%' }}
        >
          <UploadProjectCard
            isScanning={isScanning}
            onUpload={handleBatchFolderSelect}
          />
        </FadeContent>
      </Box>
    </Box>
  );
};

export default ProjectsGrid;
